/**
 * The attribute-filter row state every Dryad surface edits, plus the arity
 * predicates and shape validation derived from the shared operator vocabulary.
 *
 * Nothing here knows a domain: the three rules that vary per surface — which
 * fields refuse `one_of`, which field the email operators apply to, and how
 * long a `one_of` list may be — arrive through {@link AttributeFieldPolicy}.
 */

import type { ValidationIssue } from '@customerio/dryad';
import { familyOfOperator, isOneOfOperator, operatorArity, timestampVariantOf } from './operators';
import { isOneOfValueBlank, oneOfValueToEntries } from './one-of-value';
import { EMPTY_ATTRIBUTE_FIELD_POLICY, type AttributeFieldPolicy } from './operator-groups';
import type { JsonPathItem } from './json-attribute-path';

/**
 * A single attribute filter as the condition rows edit it. `operator` uses
 * the UI convention where negation is a '!' prefix ('!eq'); a wire format may
 * instead wrap the filter in `{ not: ... }`. `value2` is set only for the
 * between operators (bw, timestamp_bw). Unary operators carry no value — the
 * per-surface serializers drop it.
 */
export type AttributeFilterState = {
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  /**
   * The JSON path builder's levels, present only while `field` alone cannot
   * reconstruct them: a half-built level like `A.` flattens lossily. `field`
   * stays the flattened path and is all a serializer reads; the validator
   * holds a draft with an unnamed level invalid, so no host saves a path the
   * user has not finished.
   */
  jsonPathDraft?: readonly JsonPathItem[];
};

export function createDefaultAttributeFilterState(): AttributeFilterState {
  return { field: '', operator: 'eq', value: null, value2: null };
}

/**
 * Unary operators (exists, timestamp, email_valid and negations) carry no
 * value on the wire or in the UI. Arity is declared per operator, and a
 * negation inherits its base's.
 */
export function isUnaryFilterOperator(operator: string): boolean {
  return operatorArity(operator) === 0;
}

/** Between operators (bw, timestamp_bw) need value and value2. */
export function isTwoValueFilterOperator(operator: string): boolean {
  return operatorArity(operator) === 2;
}

// A switch must drop existing values when either operator is unary — into one
// because the new operator takes no value, out of one because the only value a
// unary operator can hold came off the wire and the editor never shows it.
// Also when crossing a boundary where the value semantics change: into or out
// of the range picker (timestamp_bw, epoch pair), and into or out of the
// timestamp inequalities (signed seconds offset or epoch, depending on
// variant). Within the inequality family the value carries — after/before read
// it identically. Shared by every operator-change handler so the rule can't
// drift between them.
// fallow-ignore-next-line complexity -- a flat sequence of boundary guards; splitting hurts readability
export function clearsValues(nextOperator: string, prevOperator: string): boolean {
  if (isUnaryFilterOperator(nextOperator) || isUnaryFilterOperator(prevOperator)) {
    return true;
  }
  if (nextOperator === 'timestamp_bw' || prevOperator === 'timestamp_bw') {
    return true;
  }
  return (timestampVariantOf(nextOperator) == null) !== (timestampVariantOf(prevOperator) == null);
}

function isEmailOperator(operator: string): boolean {
  return familyOfOperator(operator) === 'email';
}

/**
 * The list-cap and disallowed-field rules for one_of; empty for every other
 * operator, and for a surface whose policy declares neither rule. The
 * field-rule message asks for a different operator, so it's tagged to the
 * operator input rather than the attribute one.
 */
export function oneOfFilterErrors(
  state: AttributeFilterState,
  policy: AttributeFieldPolicy,
): Required<ValidationIssue>[] {
  const errors: Required<ValidationIssue>[] = [];
  if (!isOneOfOperator(state.operator)) {
    return errors;
  }
  const maxValues = policy.oneOfMaxValues;
  if (maxValues != null && oneOfValueToEntries(state.value).length > maxValues) {
    errors.push({ message: `Use at most ${maxValues} values.`, field: 'value' });
  }
  const name = state.field.trim();
  if (policy.oneOfDisallowedFields?.has(name) === true) {
    errors.push({
      message: `The 'is one of' operator isn't supported for ${name}. Choose a different operator.`,
      field: 'operator',
    });
  }
  return errors;
}

/**
 * Only reachable by choosing the operator first and changing the attribute
 * after — the dropdown disables the email operators off the surface's email
 * field. Untrimmed, matching the dropdown's enablement rule: a padded
 * '  email  ' is a different attribute, so the operator can't apply. A surface
 * that declares no email field gets no error.
 */
export function emailFieldError(operator: string, field: string, policy: AttributeFieldPolicy): string | undefined {
  const emailField = policy.emailField;
  if (emailField == null || !isEmailOperator(operator) || field === emailField) {
    return undefined;
  }
  return 'This operator can only be used for the email attribute.';
}

/**
 * A blank value blocks the commit on an editing surface. one_of holds its
 * entries as a marshalled list, so its blankness is the list's.
 */
export function hasBlankValue(state: AttributeFilterState): boolean {
  if (isOneOfOperator(state.operator)) {
    return isOneOfValueBlank(state.value);
  }
  return state.value == null || state.value.trim() === '';
}

/**
 * An absolute value is whatever the date picker wrote, so it is a signed
 * integer and nothing narrower: dates before 1970 serialize negative, and the
 * backend's plausible-epoch bounds belong to the "is a timestamp" *condition*,
 * not to a date the reader explicitly chose. Requiring them here rejected
 * every date before 1973-03-03 and dropped stored filters on the next apply.
 */
function isValidEpoch(timestamp: string): boolean {
  return /^-?\d+$/.test(timestamp);
}

/**
 * A timestamp inequality needs a signed-integer value (a seconds offset or an
 * epoch). Reachable only from stored data — the sub-editor can't write
 * garbage — but a claimed leaf must surface the problem instead of refusing to
 * commit silently. A relative offset is any signed integer, while an absolute
 * value — including both ends of timestamp_bw — must be a plausible unix
 * epoch. Blank passes both rules — the blank-value check owns that message.
 */
const TIMESTAMP_VALUE_RULES = {
  days: { isValid: (value: string) => /^(-?\d+)?$/.test(value), message: 'Enter a number of days.' },
  epoch: { isValid: (value: string) => value === '' || isValidEpoch(value), message: 'Choose a date.' },
} as const;

function timestampValueKind(operator: string): keyof typeof TIMESTAMP_VALUE_RULES | undefined {
  const variant = timestampVariantOf(operator);
  if (variant != null) {
    return variant === 'relative' ? 'days' : 'epoch';
  }
  return operator === 'timestamp_bw' ? 'epoch' : undefined;
}

/** Whether the operator's value is a timestamp: a number of days or a date. */
export function takesTimestampValue(operator: string): boolean {
  return timestampValueKind(operator) != null;
}

export function timestampValueError(operator: string, value: string | null): string | undefined {
  const kind = timestampValueKind(operator);
  if (kind == null || TIMESTAMP_VALUE_RULES[kind].isValid((value ?? '').trim())) {
    return undefined;
  }
  return TIMESTAMP_VALUE_RULES[kind].message;
}

/** The issue tag for a JSON path level's name, below the root. */
export function jsonPathLevelIssueField(levelIndex: number): string {
  return `jsonPath.${levelIndex}.field`;
}

/**
 * A JSON path level with no name cannot evaluate. A `'value'` level is the
 * exception: it matches the array element itself, so it has no name to give.
 * The root level is the row's own attribute, so its issue carries that tag.
 */
function jsonPathDraftIssues(draft: readonly JsonPathItem[]): Required<ValidationIssue>[] {
  return draft.flatMap((item, index) => {
    if (item.name.trim() !== '' || item.arrayConditionType === 'value') {
      return [];
    }
    return index === 0
      ? [{ message: 'Select an attribute.', field: 'field' }]
      : [{ message: 'Enter a property.', field: jsonPathLevelIssueField(index) }];
  });
}

/**
 * The attribute-name issues: the row names an attribute, and no JSON path
 * level is left unnamed. A blank field and an unnamed root level are the same
 * problem, so it is reported once.
 */
export function fieldIssues(state: AttributeFilterState): Required<ValidationIssue>[] {
  const draftIssues = state.jsonPathDraft == null ? [] : jsonPathDraftIssues(state.jsonPathDraft);
  if (state.field.trim() !== '' || draftIssues.some((issue) => issue.field === 'field')) {
    return draftIssues;
  }
  return [{ message: 'Select an attribute.', field: 'field' }, ...draftIssues];
}

// Shape-only validation shared by the plugins; no external data lookups.
// Every issue is tagged with the state property it concerns (the email/one_of
// field rules with 'operator', whose message names the fix), so a row can put
// each message on the matching input via dryad's fieldError instead of only
// counting them.
// fallow-ignore-next-line complexity -- independent per-field checks; splitting adds indirection
export function validateAttributeFilterState(
  state: AttributeFilterState,
  policy: AttributeFieldPolicy = EMPTY_ATTRIBUTE_FIELD_POLICY,
): Required<ValidationIssue>[] {
  const errors: Required<ValidationIssue>[] = fieldIssues(state);
  if (!isUnaryFilterOperator(state.operator) && hasBlankValue(state)) {
    errors.push({ message: 'Enter a value.', field: 'value' });
  }
  const timestampError = timestampValueError(state.operator, state.value);
  if (timestampError != null) {
    errors.push({ message: timestampError, field: 'value' });
  }
  const timestamp2Error = timestampValueError(state.operator, state.value2);
  if (timestamp2Error != null) {
    errors.push({ message: timestamp2Error, field: 'value2' });
  }
  if (isTwoValueFilterOperator(state.operator) && (state.value2 == null || state.value2.trim() === '')) {
    errors.push({ message: 'Enter a second value.', field: 'value2' });
  }
  const emailError = emailFieldError(state.operator, state.field, policy);
  if (emailError != null) {
    errors.push({ message: emailError, field: 'operator' });
  }
  errors.push(...oneOfFilterErrors(state, policy));
  return errors;
}
